/**
 * Device access settings API (User PINs / lock screen).
 * Endpoints: GET/PUT /api/account/device-access-settings (see docs/user-pins-api-server-spec.md).
 */

import { apiFetch } from "@common/lib/api";
import type { DeviceAccessSettings } from "@common/utils/types";

const PATH = "account/device-access-settings";

/**
 * Fetches device access settings. Throws on network/API errors or empty response so callers
 * (e.g. React Query) do not cache "off" and bypass the lock screen (fail-closed).
 * The API server requires `account_id` (query) even when JWT is present.
 */
export async function getDeviceAccessSettings(accountId: string): Promise<DeviceAccessSettings> {
    const data = await apiFetch<DeviceAccessSettings>(PATH, {
        method: "GET",
        searchParams: { account_id: accountId },
    });
    if (!data) throw new Error("Device access settings: empty response");
    return data;
}

/**
 * Fetches device access settings when the client has no session (e.g. after lock() invalidated it).
 * Sends GET account/device-access-settings without Authorization. Backend must allow this for the unlock
 * flow to detect when an admin disables the lock feature server-side.
 */
export async function getDeviceAccessSettingsForUnlock(
    accountId: string,
    deviceId: string,
): Promise<DeviceAccessSettings> {
    const data = await apiFetch<DeviceAccessSettings>(PATH, {
        method: "GET",
        skipAuth: true,
        searchParams: { account_id: accountId, device_id: deviceId },
    });
    if (!data) throw new Error("Device access settings: empty response");
    return data;
}

export async function putDeviceAccessSettings(
    accountId: string,
    payload: Partial<DeviceAccessSettings>,
): Promise<DeviceAccessSettings> {
    // Some API stacks validate `account_id` from query (same as GET); keep body in sync and
    // set account_id last so payload cannot overwrite it with undefined.
    const body = { ...payload, account_id: accountId };
    try {
        const data = await apiFetch<DeviceAccessSettings>(PATH, {
            method: "PUT",
            searchParams: { account_id: accountId },
            json: body,
        });
        if (data) return data;
    } catch (error) {
        // Some servers return 200 with an empty body; ky/json() throws SyntaxError.
        if (error instanceof SyntaxError) {
            return getDeviceAccessSettings(accountId);
        }
        throw error;
    }
    return getDeviceAccessSettings(accountId);
}
